/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable camelcase */
import { inject, Injectable } from '@angular/core'
import { Router } from '@angular/router'
import { CCGOrder as Order } from '@app/core/models/custom/order/ccg-order.model'
import { TealiumConfig, TealiumViewModel } from '@app/core/models/custom/tealium/tealium.model'
import { ActiveCartFacade, Cart } from '@spartacus/cart/base/root'
import { ActivatedRouterStateSnapshot, AuthStorageService, Category, Price, PriceRange, Product, ProductSearchPage, ProductService, User, WindowRef } from '@spartacus/core'
import { OrderFacade } from '@spartacus/order/root'
import { ProductListComponentService } from '@spartacus/storefront'
import { combineLatest, debounceTime, filter, map, Observable, Subscription, switchMap, take, takeWhile } from 'rxjs'
import { environment } from 'src/environments/environment'
import { DIALOG_NAME_MAP, tealiumCheckoutPages, tealiumPagesCustomizationMap } from '../../../core/config/tealium/tealium.constants'
import { CustomCartService } from '../../cms-components/cart/services/cart.service'
import { SpectrumPlacementPositions } from '../../components/modals/product-customizer-modal/models/product-customizer-modal.model'
import { CustomUserService } from '../user.service'
import { WindowService } from '../window.service'

declare global {
  interface Window {
    utag?: {
      view: (data?: unknown) => void;
      track: (event: string, data?: unknown) => void;
    };
    utag_cfg_ovrd?: Record<string, any>;
    utag_data?: Record<string, any>;
  }
}

@Injectable({
  providedIn: 'root'
})
export class TealiumUtagService {
  private readonly customUserService = inject(CustomUserService)
  private readonly router = inject(Router)
  private readonly windowRef = inject(WindowRef)
  private readonly windowService = inject(WindowService)
  private readonly cartService = inject(ActiveCartFacade)
  private readonly productService = inject(ProductService)
  private readonly productListComponentService = inject(ProductListComponentService)
  private readonly orderService = inject(OrderFacade)
  private readonly customCartService = inject(CustomCartService)
  private readonly authStorageService = inject(AuthStorageService)
  private searchPageViewSubscription: Subscription | null = null

  scriptSrc: string = ''
  scriptLoaded = false
  customerEmail: string = ''
  CURRENCY_STRING: string = 'USD'

  public setTealiumConfiguration (config: TealiumConfig): void {
    if (this.windowRef.isBrowser()) {
      const win = this.windowRef.nativeWindow
      if (win) {
        win.utag_cfg_ovrd = { noview: true }
        win.utag_data = {}
      }
      this.scriptSrc = 'https://tags.tiqcdn.com/utag/' + config.account + '/' + config.profile + '/' + config.environment + '/utag.js'
    }
  }

  public loadTealiumScript (config: TealiumConfig, callback?: () => void): void {
    if (this.windowRef.isBrowser()) {
      const windowDocument = this.windowRef.document

      if (this.scriptLoaded || windowDocument.getElementById('tealium-script')) {
        return
      }

      this.scriptSrc = `https://tags.tiqcdn.com/utag/${config.account}/${config.profile}/${config.environment}/utag.js`
      const script = document.createElement('script')
      script.id = 'tealium-script'
      script.type = 'text/javascript'
      script.async = true
      script.src = this.scriptSrc

      script.onload = () => {
        this.scriptLoaded = true
        if (callback) {
          callback()
        }
      }

      script.onerror = () => {
        console.error('Failed to load Tealium script.')
      }

      const head = windowDocument.getElementsByTagName('head')[0]
      if (head.firstChild) {
        head.insertBefore(script, head.firstChild)
      } else {
        head.appendChild(script)
      }
    }
  }

  view (activeRoute: ActivatedRouterStateSnapshot): void {
    this.cartService.isStable().pipe(
      takeWhile(isStable => !isStable, true), // wait until stable
      filter(isStable => !!isStable),
      switchMap(() =>
        combineLatest([
          this.customUserService.user$.pipe(take(1)),
          this.cartService.getActive().pipe(take(1))
        ])
      )
    ).subscribe(([user, cart]) => {
      const pageViewInfo: TealiumViewModel = {
        page_name: this.getPageName(activeRoute.context?.id, activeRoute.context?.type)?.toLowerCase(),
        page_type: this.getPageType(activeRoute.context?.id, activeRoute.context?.type),
        ...this.convertParamsIntoUnderscoreVariables(activeRoute.params),
        ...this.appendAsmSessionDetails()
      }

      // Customer ID, orgUnitId, order_total_quantity, login_status
      pageViewInfo.customer_id = user?.customerId ?? ''
      pageViewInfo.orgEntityId = user?.orgUnit?.uid ?? ''
      pageViewInfo.order_total_quantity = this.customCartService.getCartTotalItems(cart).toString()
      pageViewInfo.login_status = this.getLoginStatus()

      this.customerEmail = user?.email || ''

      // PageView: Search Page
      if (pageViewInfo.page_name?.toLowerCase() === 'search') {
        if (this.searchPageViewSubscription) {
          this.searchPageViewSubscription.unsubscribe()
          this.searchPageViewSubscription = null
        }

        this.searchPageViewSubscription = this.handleSearchPageView(activeRoute, pageViewInfo)
          .pipe(take(1))
          .subscribe(fullPageViewInfo => {
            this.track('view', fullPageViewInfo)
            this.searchPageViewSubscription = null
          })

        return
      }

      // PageView: Category Page
      if (pageViewInfo.page_type?.toLowerCase() === 'categorypage') {
        this.handleCategoryPagePageView(activeRoute, pageViewInfo)
          .subscribe(fullPageViewInfo => {
            this.track('view', fullPageViewInfo)
          })

        return
      }

      // PageView: Product Page
      if (pageViewInfo.page_type?.toLowerCase() === 'productpage') {
        this.handleProductDetailsPageView(activeRoute, pageViewInfo)
          .subscribe(fullPageViewInfo => {
            this.track('view', fullPageViewInfo)
          })

        return
      }

      // PageView: Cart/Checkout Page
      if (activeRoute.context?.id && tealiumCheckoutPages.indexOf(activeRoute.context.id.replace(/^\/+/, '').toLowerCase()) !== -1) {
        this.handleCheckoutPageView(pageViewInfo)
          .subscribe(fullPageViewInfo => {
            this.track('view', fullPageViewInfo)
          })

        return
      }

      // PageView: Order Confirmation Page
      if (activeRoute.context?.id === '/checkout/confirmation') {
        this.handleOrderConfirmationPageView(pageViewInfo)
          .subscribe(fullPageViewInfo => {
            this.track('view', fullPageViewInfo)
          })

        return
      }

      // PageView: default
      const fullPageViewInfo = {
        ...pageViewInfo,
        ...this.customPageViewByPageName(pageViewInfo.page_name ?? '')
      }

      this.track('view', fullPageViewInfo)
    })
  }

  viewModal (caller: string): void {
    const pageViewInfo = {
      pageName: DIALOG_NAME_MAP[caller],
      pageType: 'modal'
    }

    this.track('view', pageViewInfo)
  }

  track(tealiumEvent: string, data?: unknown | TealiumViewModel) {
    if (!this.windowRef.isBrowser()) return;
    const win = this.windowRef.nativeWindow;
    if (!win) return;
    if (this.scriptSrc === '') {
      console.error('Tealium config not set.');
      return;
    }
    //@ts-ignore
    const hasRealUtag = !!(win.utag && win.utag?.cfg);
    if (!hasRealUtag) {
      this.loadTealiumScript({
        environment: environment.tealiumEnvironment,
        account: environment.tealiumAccount,
        profile: environment.tealiumProfile
      });
      // If the 4.52+ queue stub exists, this call will be queued
      if (win.utag) {
        this.trackingFunction(tealiumEvent, data);
      }
      return;
    }
    this.trackingFunction(tealiumEvent, data);
  }
  trackModalViewEvent (modelContext: string) {
    this.view({
      context: {
        id: modelContext,
        type: 'Account'
      }
    } as unknown as ActivatedRouterStateSnapshot)
  }

  trackingFunction (tealiumEvent: string, data?: unknown | TealiumViewModel) {
    const win = this.windowRef.nativeWindow

    if (!win || !win['utag']) {
      console.warn('Tealium is not initialized or running in SSR')
      return
    }

    if (tealiumEvent === 'view') {
      win['utag'].view(data)
    } else {
      win['utag'].track(tealiumEvent, data)
    }
  }

  // Search page
  private handleSearchPageView (
    activeRoute: ActivatedRouterStateSnapshot,
    pageViewInfo: TealiumViewModel
  ): Observable<TealiumViewModel> {
    const query = activeRoute.params['query']

    return this.productListComponentService.model$.pipe(
      filter(searchData => searchData.Products?.freeTextSearch === query),
      debounceTime(2000),
      take(1),
      map(searchData => {
        const productCount = searchData.Products?.pagination?.totalResults ?? 0
        const contentCount = searchData.Content?.pagination?.totalNumberOfResults ?? 0
        const totalResults = productCount + contentCount

        return {
          ...pageViewInfo,
          page_name: (totalResults > 0 ? 'search results' : 'no results').toLowerCase(),
          page_type: 'search results',
          search_keyword: query,
          search_results: totalResults
        }
      })
    )
  }

  // PLP
  private handleCategoryPagePageView (
    activeRoute: ActivatedRouterStateSnapshot,
    pageViewInfo: TealiumViewModel
  ): Observable<TealiumViewModel> {
    return this.productListComponentService.model$.pipe(
      filter(searchData => {
        return (searchData as ProductSearchPage)?.Products?.category?.code === activeRoute?.context?.id
      }),
      take(1),
      map((searchData) => {
        const category = (searchData as ProductSearchPage)?.Products?.category
        return {
          ...pageViewInfo,
          ...this.customPageViewCategoryPage(pageViewInfo, category, activeRoute)
        }
      })
    )
  }

  // Custom PLP
  private customPageViewCategoryPage (pageViewInfo: TealiumViewModel, category?: Category, activeRoute?: ActivatedRouterStateSnapshot): TealiumViewModel {
    const categoryName = category?.name || category?.seoMetaTitle || pageViewInfo.page_name || ''

    let categoryPathParts: string[] = []
    if (activeRoute?.url) {
      const urlSegments = activeRoute.url.split('/').filter(segment => segment !== '')
      categoryPathParts = urlSegments.filter((segment, index) => segment !== 'c' && index !== urlSegments.length - 1)
    }

    const pageViewObject: TealiumViewModel = {
      page_name: categoryName?.toLowerCase(),
      page_category: categoryPathParts[0] || ''
    }

    if (categoryPathParts.length > 1) {
      pageViewObject.page_type = 'subcategory'
      pageViewObject.page_subcategory = categoryPathParts[1] || ''
    } else {
      pageViewObject.page_type = 'category'
    }

    if (categoryPathParts.length > 2) {
      pageViewObject.page_subcategory2 = categoryPathParts[2] || ''
    }

    return pageViewObject
  }

  // PDP
  private handleProductDetailsPageView (
    activeRoute: ActivatedRouterStateSnapshot,
    pageViewInfo: TealiumViewModel
  ): Observable<TealiumViewModel> {
    return this.productService.get(activeRoute.context.id).pipe(
      filter((product): product is Product => !!product && Object.keys(product).length > 0),
      take(1),
      switchMap((product) =>
        this.customPageViewProductPage(product).pipe(
          map(customPageData => ({
            ...pageViewInfo,
            ...customPageData
          }))
        )
      )
    )
  }

  // Custom PDP
  private customPageViewProductPage (product: Product): Observable<TealiumViewModel> {
    const baseProduct = product.baseProduct || product
    const manufacturerAID = product.manufacturerAID || (typeof baseProduct === 'object' ? baseProduct.manufacturerAID : '')
    const productCode = product.code || ''
    const baseProductCode = typeof baseProduct === 'object' ? baseProduct.code : baseProduct
    const baseProductSku = typeof baseProduct === 'object' ? baseProduct.code : baseProduct
    const isLicensedProduct = this.isLicensedProduct(baseProductSku || '')

    return combineLatest([
      this.customUserService.user$.pipe(take(1)),
      this.cartService.getActive().pipe(take(1))
    ]).pipe(
      map(([user, cart]) => {
        let quantity = ['1']

        if (cart?.entries) {
          const matchingEntries = cart.entries.filter(entry => entry.product?.baseProduct === baseProductCode)

          if (matchingEntries.length > 0) {
            const totalQuantity = matchingEntries.reduce((sum, entry) => sum + (entry.quantity || 0), 0)
            quantity = [totalQuantity.toString()]
          }
        }

        return {
          page_name: `product: ${product.name} ${manufacturerAID || baseProductCode}`.toLowerCase(),
          page_type: 'product detail page',
          product_vendor_style_id: isLicensedProduct ? [manufacturerAID || ''] : [null],
          style_id: baseProductSku || '',
          product_style: [baseProductSku || ''],
          ...this.getProductPrice(product),
          product_quantity: quantity,
          product_dynamic_sku: [manufacturerAID ? manufacturerAID : productCode],
          product_sku: [baseProductSku || ''],
          customer_id: user?.customerId || '',
          vendor_style_id: isLicensedProduct && manufacturerAID ? manufacturerAID : "''",
          orgEntityId: user?.orgUnit?.uid || '',
          product_discount: this.calculateProductDiscount(product)
        }
      })
    )
  }

  // Cart / Checkout
  private handleCheckoutPageView (
    pageViewInfo: TealiumViewModel
  ): Observable<TealiumViewModel> {
    return this.cartService.getActive().pipe(
      filter(cart => cart && Object.keys(cart).length > 0),
      take(1),
      map((cart) => ({
        ...pageViewInfo,
        ...this.customPageViewCheckoutPage(cart, pageViewInfo)
      }))
    )
  }

  // Custom Cart / Checkout
  private customPageViewCheckoutPage (cart: Cart, pageViewInfo: TealiumViewModel): TealiumViewModel {
    const basePageViewData: TealiumViewModel = {
      ...pageViewInfo,
      ...this.customPageViewByPageName(pageViewInfo.page_name ?? '')
    }

    const validEntries = this.getValidEntriesFromGroups(cart)
    const productStyles = validEntries.map(entry => entry.product?.baseProduct || '') || []
    const productPrices = validEntries.map(entry => entry.product?.price?.value?.toString() || '') || []
    const productRegularPrices = validEntries.map(entry => this.getProductRegularPrice(entry.product)) || []
    const productQuantities = validEntries.map(entry => entry.quantity?.toString() || '') || []
    const productSkus = validEntries.map(entry => entry.product?.code || '') || []
    const productBygLocations = this.getPlacementsFromEntries(validEntries)
    const productDiscounts = validEntries.map(entry => this.calculateProductDiscount(entry.product || {} as Product)[0])
    const productVendorStyleIds = validEntries.map(entry => {
      const baseProductSku = entry.product?.baseProduct || ''
      const manufacturerAID = entry.product?.manufacturerAID || ''
      const isLicensed = this.isLicensedProduct(baseProductSku)
      return isLicensed ? manufacturerAID : null
    })
    const productProof = validEntries.map(entry => {
      const bygType = this.getBygType(entry.recipeInfo?.serviceType)
      const group = cart.ccgEntryGroups?.find(g => g.entries?.some(e => e === entry))
      const requestProof = group?.requestProof || false
      return this.getProductProofValue(bygType, requestProof)
    }) || []

    return {
      ...basePageViewData,
      cartId: cart.code,
      order_subtotal: this.calculateSubtotal(cart, false),
      product_currency_string: cart.subTotal?.currencyIso || this.CURRENCY_STRING,
      product_currency: [cart.subTotal?.currencyIso || this.CURRENCY_STRING],
      product_discount: productDiscounts,
      product_vendor_style_id: productVendorStyleIds,
      product_style: productStyles,
      product_byg_type: validEntries.map(entry => this.getBygType(entry.recipeInfo?.serviceType)) || [],
      product_byg_location: productBygLocations,
      product_price: productPrices,
      product_regular_price: productRegularPrices,
      product_quantity: productQuantities,
      product_sku: productSkus,
      product_proof: productProof
    }
  }

  // Order Confirmation
  handleOrderConfirmationPageView (
    pageViewInfo: TealiumViewModel
  ): Observable<TealiumViewModel> {
    return this.orderService.getOrderDetails().pipe(
      filter(order => !!order && Object.keys(order).length > 0),
      take(1),
      map(order => ({
        ...pageViewInfo,
        ...this.customPageViewByConfirmationPage(order as Order, pageViewInfo)
      }))
    )
  }

  customPageViewByConfirmationPage (order: Order, pageViewInfo: TealiumViewModel) {
    const basePageViewData: TealiumViewModel = {
      ...pageViewInfo,
      ...this.customPageViewByPageName(pageViewInfo.page_name ?? '')
    }

    const validEntries = this.getValidEntriesFromGroups(order)
    const bygValues = this.calculateBygValues(validEntries, order.groupedFeeProductResults)

    const productDiscounts = validEntries.map(entry =>
      this.calculateProductDiscount(entry.product)[0]
    )

    const productVendorStyleIds = validEntries.map(entry => {
      const baseProductSku = entry.product?.baseProduct || ''
      const manufacturerAID = entry.product?.manufacturerAID || ''
      const isLicensed = this.isLicensedProduct(baseProductSku)
      return isLicensed ? manufacturerAID : null
    })

    const productProof = validEntries.map(entry => {
      const bygType = this.getBygType(entry.recipeInfo?.serviceType)
      const group = order.ccgEntryGroups?.find(g => g.entries?.some(e => e === entry))
      const requestProof = group?.requestProof || false
      return this.getProductProofValue(bygType, requestProof)
    }) || []

    const productPromoAdjustments = validEntries.map(entry => {
      let totalPromoAdjustment = 0

      if (!entry.product?.price?.value || !entry.quantity) {
        return '0.00'
      }

      if (order.appliedProductPromotions?.length) {
        order.appliedProductPromotions.forEach(promo => {
          if (promo.consumedEntries?.length) {
            promo.consumedEntries.forEach(consumedEntry => {
              if (entry.entryNumber === consumedEntry.orderEntryNumber) {
                const originalPrice = entry.product.price.value
                const adjustedUnitPrice = consumedEntry.adjustedUnitPrice || 0
                const quantity = consumedEntry.quantity || 0
                const promoAdjustment = (originalPrice - adjustedUnitPrice) * quantity
                totalPromoAdjustment += promoAdjustment
              }
            })
          }
        })
      }

      return totalPromoAdjustment > 0 ? `-${totalPromoAdjustment.toFixed(2)}` : '0.00'
    })

    const productPromoTotal = productPromoAdjustments
      .reduce((sum, adjustment) => sum + Math.abs(parseFloat(adjustment)), 0)
      .toFixed(2)

    const appliedPromotions = [
      ...order.appliedOrderPromotions || [],
      ...order.appliedVouchers || []
    ]

    const promoCodes = appliedPromotions
      .map(promo => {
        if ('code' in promo) {
          return promo.code
        }
        if ('voucherCode' in promo) {
          return promo.voucherCode
        }
        return ''
      })
      .filter(Boolean)
      .join(',')

    const promoNames = appliedPromotions
      .map(promo => {
        if ('description' in promo) {
          return promo.description
        }
        return ''
      })
      .filter(Boolean)
      .join(',')

    const productBygLocations = validEntries.map(entry => {
      if (!entry.recipeInfo?.placements?.length) {
        return null
      }
      return entry.recipeInfo.placements.map((placement: any) => placement.placement).filter(Boolean).join('-')
    })

    return {
      ...basePageViewData,
      customer_country: order.orgCustomer?.defaultAddress?.country?.isocode ?? '',
      customer_email: order.orgCustomer?.email ?? '',
      customer_zipcode: order.orgCustomer?.defaultAddress?.postalCode ?? '',
      ...bygValues,
      order_discount_amount: `-${order.totalDiscounts?.value?.toFixed(2).toString() ?? ''}`,
      order_shipping_discount: `-${order.deliveryCost?.value?.toFixed(2).toString() ?? ''}`,
      order_employee_discount: this.extractTrueValue(order.employeeSpecificPromotionDiscount) ?? '',
      order_grand_total: order.totalPriceWithTax?.value?.toString() ?? '',
      orderId: order.code,
      order_payment_type: order.paymentInfo?.cardType?.name || ((order as any).echeckPaymentInfo ? 'ECHECK' : ''),
      order_promo_adjustment: `-${order.orderDiscounts?.value?.toFixed(2).toString() ?? ''}`,
      order_promo_code: promoCodes,
      order_private_promo: (order.appliedPromotionsWithoutCoupons || []).join(','),
      order_promo_name: promoNames,
      order_shipping_amount: order.deliveryFee?.value?.toString() ?? '',
      order_shipping_type: order.deliveryMode?.name ?? '',
      order_subtotal: this.calculateSubtotal(order, true),
      order_tax_amount: order.totalTax?.value?.toString() ?? '',
      order_total_quantity: this.customCartService.getCartTotalItems(order as Cart).toString(),
      product_currency_string: order.subTotal?.currencyIso || this.CURRENCY_STRING,
      product_currency: [order.subTotal?.currencyIso || this.CURRENCY_STRING],
      product_discount: productDiscounts,
      product_vendor_style_id: productVendorStyleIds,
      product_style: validEntries.map(entry => entry.product?.baseProduct || '') || [],
      product_byg_type: validEntries.map(entry => this.getBygType(entry.recipeInfo?.serviceType)) || [],
      product_byg_location: productBygLocations,
      product_price: validEntries.map(entry => entry.product?.price?.value?.toString() || '') || [],
      product_regular_price: validEntries.map(entry => this.getProductRegularPrice(entry.product)) || [],
      product_quantity: validEntries.map(entry => entry.quantity?.toString() || '') || [],
      product_sku: validEntries.map(entry => entry.product?.code || '') || [],
      product_promo_adjustment: productPromoAdjustments,
      product_promo_total: productPromoTotal,
      product_proof: productProof
    }
  }

  // Track Add & Remove from Cart
  trackCartAction (product: Product, quantity: number, origin: string[], type: 'add' | 'remove', orderEntries?: any[]): void {
    this.cartService.getActive().pipe(take(1)).subscribe(cart => {
      const isCartQuantityUpdate = origin.includes('quantity_dropdown') || origin.includes('quantity_manual_input')
      const isEntireCartDelete = origin.includes('delete_entire_cart')

      let entries: any[] = []

      if (orderEntries?.length) {
        if (isEntireCartDelete) {
          const placementCodes = Object.keys(SpectrumPlacementPositions)
          entries = orderEntries.filter(entry =>
            entry.product?.code && !entry.product.code.includes('SETUPCHARGE') && !entry.product.code.includes('SPSETUPCHARGE') && !placementCodes.includes(entry.product.code)
          )
        } else {
          entries = orderEntries
        }
      } else if (isCartQuantityUpdate) {
        if (orderEntries?.length) {
          entries = orderEntries
        } else {
          const actualCartEntry = cart?.entries?.find(entry => entry.product?.code === product.code)
          if (actualCartEntry) {
            entries = [actualCartEntry]
          } else {
            entries = [{
              product: product,
              quantity: quantity,
              price: product.price,
              totalPrice: product.price
            }]
          }
        }
      } else {
        entries = cart?.entries?.filter(entry => entry.product?.code === product.code) || []
      }

      this.customUserService.user$.pipe(take(1)).subscribe(user => {
        const trackingData: Partial<TealiumViewModel> = {
          event_name: type === 'add' ? 'cart_add' : 'cart_remove',
          origin: this.getOriginValue(origin),
          customer_id: user?.customerId || '',
          orgEntityId: user?.orgUnit?.uid || '',
          product_currency_string: product?.price?.currencyIso || this.CURRENCY_STRING,
          product_currency: [product?.price?.currencyIso || this.CURRENCY_STRING],
          ...this.appendAsmSessionDetails()
        }

        if (entries.length > 0) {
          trackingData.product_price = entries.map(entry => {
            const price = (entry as any).variantPrice?.value || entry.product?.price?.value || entry.price?.value || entry.totalPrice?.value || product?.price?.value || 0
            return price.toString()
          })

          trackingData.product_regular_price = entries.map(() => {
            return this.getProductRegularPrice(product)
          })

          if (isCartQuantityUpdate) {
            let quantityToTrack: string
            if (origin.includes('quantity_dropdown')) {
              quantityToTrack = '1'
            } else {
              quantityToTrack = quantity.toString()
            }

            trackingData.product_quantity = [quantityToTrack]
            const singleEntry = entries[0]
            trackingData.product_price = [(singleEntry as any).variantPrice?.value || singleEntry.product?.price?.value || singleEntry.price?.value || singleEntry.totalPrice?.value || product?.price?.value || 0].map(price => price.toString())
            trackingData.product_regular_price = [this.getProductRegularPrice(product)]
            trackingData.product_sku = [singleEntry.product?.code || '']
            trackingData.product_style = [singleEntry.product?.baseProduct || product?.baseProduct || '']
            trackingData.product_byg_type = [this.getBygType(singleEntry.recipeInfo?.serviceType)]
            trackingData.product_byg_location = this.getPlacementsFromEntries([singleEntry])
            trackingData.product_vendor_style_id = [
              (() => {
                const baseProductSku = singleEntry.product?.baseProduct || product?.baseProduct || ''
                const manufacturerAID = singleEntry.product?.manufacturerAID || product?.manufacturerAID || ''
                const isLicensed = this.isLicensedProduct(baseProductSku)
                return isLicensed && manufacturerAID && manufacturerAID.trim() !== '' ? manufacturerAID : null
              })()
            ]
          } else {
            trackingData.product_quantity = entries.map(entry => entry.quantity?.toString() || '')
            trackingData.product_sku = entries.map(entry => entry.product?.code || '')
            trackingData.product_style = entries.map(entry => {
              const baseProduct = entry.product?.baseProduct || product?.baseProduct
              return baseProduct && baseProduct.trim() !== '' ? baseProduct : ''
            })
            trackingData.product_regular_price = entries.map(() => this.getProductRegularPrice(product))
            trackingData.product_byg_type = entries.map(entry => this.getBygType(entry.recipeInfo?.serviceType))
            trackingData.product_byg_location = this.getPlacementsFromEntries(entries)
            trackingData.product_vendor_style_id = entries.map(entry => {
              const baseProductSku = entry.product?.baseProduct || product?.baseProduct || ''
              const manufacturerAID = entry.product?.manufacturerAID || product?.manufacturerAID || ''
              const isLicensed = this.isLicensedProduct(baseProductSku)
              return isLicensed && manufacturerAID && manufacturerAID.trim() !== '' ? manufacturerAID : null
            })
          }
        } else {
          trackingData.product_price = [product?.price?.value?.toString() || '']
          trackingData.product_regular_price = [this.getProductRegularPrice(product)]
          trackingData.product_quantity = [quantity.toString()]
          trackingData.product_sku = [product?.code || '']
          trackingData.product_style = [
            product?.baseProduct && product.baseProduct.trim() !== '' ? product.baseProduct : ''
          ]
          trackingData.product_byg_type = [this.getBygType((product as any)?.recipeInfo?.serviceType)]
          trackingData.product_byg_location = this.getPlacementsFromEntries([])

          const baseProductSku = product?.baseProduct || ''
          const manufacturerAID = product?.manufacturerAID || ''
          const isLicensed = this.isLicensedProduct(baseProductSku)
          trackingData.product_vendor_style_id = [
            isLicensed && manufacturerAID && manufacturerAID.trim() !== '' ? manufacturerAID : null
          ]
        }

        this.track('link', trackingData)
      })
    })
  }

  // Track Promo Code
  trackPromoCode (promoCode: string): void {
    this.customUserService.user$.pipe(take(1)).subscribe(user => {
      const currentRoute = this.router.url
      const pageName = tealiumPagesCustomizationMap[currentRoute]?.pageName || currentRoute

      this.track('link', {
        event_name: 'add_promo_code',
        origin: pageName,
        customer_id: user?.customerId || '',
        orgEntityId: user?.orgUnit?.uid || '',
        promo_code: promoCode
      })
    })
  }

  // Track Login
  trackLogin (user: User): void {
    this.track('link', {
      event_name: 'user_login',
      login_status: this.getLoginStatus(),
      customer_id: user?.customerId || '',
      orgEntityId: user?.orgUnit?.uid || '',
      ...this.appendAsmSessionDetails()
    })
  }

  // Track Login Initiation
  trackLoginInitiation (): void {
    this.track('link', {
      event_name: 'login_initiation'
    })
  }

  // Track Login Modal View
  trackLoginModalView (context: string): void {
    this.customUserService.user$.pipe(take(1)).subscribe(user => {
      this.cartService.getActive().pipe(take(1)).subscribe(cart => {
        const pageName = context ? `modal: login; ${context}`.toLowerCase() : 'modal: login'
        this.track('view', {
          customer_id: user?.customerId ?? '',
          orgEntityId: user?.orgUnit?.uid ?? '',
          order_total_quantity: this.customCartService.getCartTotalItems(cart).toString(),
          login_status: this.getLoginStatus(),
          page_name: pageName,
          page_type: 'account'
        })
      })
    })
  }

  // Track Mini Cart Modal View
  trackMiniCartModalView (): void {
    this.customUserService.user$.pipe(take(1)).subscribe(user => {
      this.cartService.getActive().pipe(take(1)).subscribe(cart => {
        this.track('view', {
          customer_id: user?.customerId ?? '',
          orgEntityId: user?.orgUnit?.uid ?? '',
          order_total_quantity: this.customCartService.getCartTotalItems(cart).toString(),
          login_status: this.getLoginStatus(),
          page_name: 'modal: mini-cart',
          page_type: 'account'
        })
      })
    })
  }

  // Track Registration
  trackRegistration (): void {
    this.customUserService.user$.pipe(take(1)).subscribe(user => {
      this.track('link', {
        event_name: 'user_register',
        login_status: this.getLoginStatus(),
        customer_id: user?.customerId || '',
        orgEntityId: user?.orgUnit?.uid || '',
        ...this.appendAsmSessionDetails()
      })
    })
  }

  // Track Registration Initiation
  trackRegistrationInitiation (formData: { companyName?: string, companySize?: string, industry?: string }): void {
    this.track('link', {
      event_name: 'account_initiation',
      ccg_company_name: formData.companyName || '',
      ccg_company_industry: formData.industry || '',
      ccg_company_size: formData.companySize || ''
    })
  }

  // Track Logout
  trackLogout (status: 'click_attempt' | 'successful'): void {
    this.track('link', {
      event_name: 'user_logout',
      login_status: 'logged-out',
      logout_status: status
    })
  }

  // Track Email Signup
  trackEmailSignup (email: string, location: string): void {
    this.track('link', {
      event_name: 'email_signup',
      email_signup_locations: location,
      origin: location,
      email_address: email,
      ...this.appendAsmSessionDetails()
    })
  }

  // Default
  private customPageViewByPageName (pageName: string): Partial<TealiumViewModel> | TealiumViewModel {
    if (pageName && tealiumPagesCustomizationMap[pageName]) {
      return {
        page_name: tealiumPagesCustomizationMap[pageName].pageName?.toLowerCase(),
        page_type: tealiumPagesCustomizationMap[pageName].pageType
      }
    } else {
      return {}
    }
  }

  // HELPERS
  private convertParamsIntoUnderscoreVariables (params: { [key: string]: string }): { [key: string]: string } {
    if (params) {
      return Object
        .keys(params)
        .map(key => [key, this.convertCamelCaseToUnderscore(key)])
        .reduce(
          (accum, [originalKey, underscoreKey]) => ({ ...accum, [underscoreKey]: params[originalKey] }),
          {} as { [key: string]: string }
        )
    } else {
      return params
    }
  }

  private convertCamelCaseToUnderscore (camelCase: string): string {
    return camelCase
      .replace(
        /\.?([A-Z])/g,
        (previousString, followingString) => '_' + followingString.toLowerCase()
      )
      .replace(/^_/, '')
  }

  private getProductPrice (product: Product): Partial<TealiumViewModel> {
    const price = product.price
    const priceRange = product.priceRange

    let product_price: string | undefined
    const product_regular_price: string = this.getProductRegularPrice(product)

    if (priceRange) {
      product_price = this.calculatePriceRange(priceRange)
    } else if (product.baseProduct && price) {
      product_price = (price.value || 0).toFixed(2)
    }

    return {
      ...product_price ? { product_price: [product_price] } : {},
      ...product_regular_price ? { product_regular_price: [product_regular_price] } : {}
    }
  }

  private calculatePriceRange (priceRage: PriceRange): string {
    const priceToTransform: Price = {
      value: priceRage.minPrice?.value
    }
    return (priceToTransform.value || 0).toFixed(2)
  }

  private calculateBygValues (entries: any[], groupedFeeProductResults?: any[]): {
    byg_embroidery_setup_units: string,
    byg_embroidery_product_units: string,
    byg_screenprint_product_units: string,
    byg_screenprint_setup_units: string,
    byg_embroidery_product_rev: string,
    byg_embroidery_setup_rev: string,
    byg_screenprint_product_rev: string,
    byg_screenprint_setup_rev: string,
    product_byg_location: string[]
  } {
    let embroiderySetupUnits = 0
    let embroideryProductUnits = 0
    let screenprintProductUnits = 0
    let screenprintSetupUnits = 0
    let embroideryProductRev = 0
    let embroiderySetupRev = 0
    let screenprintProductRev = 0
    let screenprintSetupRev = 0
    const bygLocations = new Set<string>()

    entries.forEach(entry => {
      if (entry.recipeInfo?.placements) {
        entry.recipeInfo.placements.forEach((placement: any) => {
          bygLocations.add(placement.placement)
        })
      }
    })

    if (groupedFeeProductResults) {
      groupedFeeProductResults.forEach(feeEntry => {
        if (feeEntry.code === 'LOCATION-EMB') {
          embroideryProductUnits += feeEntry.quantity || 0
          embroideryProductRev += feeEntry.price?.value || 0
        } else if (feeEntry.code === 'SETUP-EMB') {
          embroiderySetupUnits += feeEntry.quantity || 0
          embroiderySetupRev += feeEntry.price?.value || 0
        } else if (feeEntry.code === 'LOCATION-SCR') {
          screenprintProductUnits += feeEntry.quantity || 0
          screenprintProductRev += feeEntry.price?.value || 0
        } else if (feeEntry.code === 'SETUP-SCR') {
          screenprintSetupUnits += feeEntry.quantity || 0
          screenprintSetupRev += feeEntry.price?.value || 0
        }
      })
    }

    return {
      byg_embroidery_setup_units: embroiderySetupUnits.toString(),
      byg_embroidery_product_units: embroideryProductUnits.toString(),
      byg_screenprint_product_units: screenprintProductUnits.toString(),
      byg_screenprint_setup_units: screenprintSetupUnits.toString(),
      byg_embroidery_product_rev: embroideryProductRev.toFixed(2),
      byg_embroidery_setup_rev: embroiderySetupRev.toFixed(2),
      byg_screenprint_product_rev: screenprintProductRev.toFixed(2),
      byg_screenprint_setup_rev: screenprintSetupRev.toFixed(2),
      product_byg_location: Array.from(bygLocations)
    }
  }

  private getPlacementsFromEntries (entries: any[]): (string | null)[] {
    return entries.map(entry => {
      if (!entry.recipeInfo?.placements?.length) {
        return null
      }
      const sortedPlacements = entry.recipeInfo.placements
        .map((placement: any) => {
          const placementValue = placement.placement
          if (!placementValue) return null

          if (entry.recipeInfo?.serviceType?.includes('SCR')) {
            return placementValue.startsWith('SP') ? placementValue : `SP${placementValue}`
          }

          return placementValue
        })
        .filter(Boolean)
        .sort()
      return sortedPlacements.join('-')
    })
  }

  private getBygType (serviceType?: string): string | null {
    if (!serviceType) return null
    if (serviceType.includes('EMB')) return 'LOCATION'
    if (serviceType.includes('SCR')) return 'SPLOCATION'
    return null
  }

  private calculateProductDiscount (product: Product): string[] {
    if (!product.price?.value) {
      return ['0']
    }

    const msrpValue = product.msrp?.value ?? product.msrpRange?.minPrice?.value ?? 0
    const price = product.price?.value ?? 0

    const discount = msrpValue - price

    return [discount > 0 ? discount.toFixed(2) : '0']
  }

  private getPageName (id?: string, type?: string): string {
    if (!id) {
      return ''
    }

    const cleanId = id.replace(/^\/+/, '')

    if (tealiumPagesCustomizationMap[id] || tealiumPagesCustomizationMap[cleanId]) {
      const mapping = tealiumPagesCustomizationMap[id] || tealiumPagesCustomizationMap[cleanId]
      return mapping.pageName
    }

    if (cleanId === 'register') {
      return 'account: register'
    }

    if (cleanId.startsWith('my-account/') || cleanId.startsWith('my-company/')) {
      const subPath = cleanId.startsWith('my-account/')
        ? cleanId.replace('my-account/', '')
        : cleanId.replace('my-company/', '')

      if (subPath.startsWith('order/')) {
        return 'account: order details'
      }

      if (subPath.startsWith('saved-gear/')) {
        return 'account: saved gear details'
      }

      return `account: ${subPath.replace(/[-_]/g, ' ')}`
    }

    if (type === 'ContentPage') {
      return cleanId.replace(/[-_]/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
    }

    return cleanId
  }

  private getPageType (id?: string, type?: string): string {
    if (!id) {
      return type || ''
    }

    const cleanId = id.replace(/^\/+/, '')

    if (tealiumPagesCustomizationMap[id] || tealiumPagesCustomizationMap[cleanId]) {
      const mapping = tealiumPagesCustomizationMap[id] || tealiumPagesCustomizationMap[cleanId]
      return mapping.pageType
    }

    if (cleanId === 'register' || cleanId.startsWith('my-account/') || cleanId.startsWith('my-company/')) {
      return 'account'
    }

    if (tealiumCheckoutPages.indexOf(cleanId.toLowerCase()) !== -1) {
      return 'checkout'
    }

    if (type === 'ContentPage') {
      return 'contentPage'
    }

    return type || ''
  }

  private getValidEntriesFromGroups (cartOrOrder: Cart | Order): any[] {
    const validEntryGroups = cartOrOrder.ccgEntryGroups || []

    return validEntryGroups.flatMap(group =>
      group.entries?.filter(entry => {
        const hasBaseOptions = entry.product?.baseOptions && entry.product.baseOptions.length > 0
        const hasRecipeInfo = !!entry.recipeInfo
        const isBygOrder = cartOrOrder.ccgEntryGroups && cartOrOrder.ccgEntryGroups.length > 0

        return hasBaseOptions || hasRecipeInfo || isBygOrder
      }) || []
    )
  }

  private getOriginValue (origin: string[]): string {
    if (origin.includes('pdp') && origin.includes('sticky')) {
      return 'pdp_sticky_cta'
    }

    const originValue = origin.length > 0 ? origin[0] : ''
    const originMapping: Record<string, string> = {
      'quantity-dropdown': 'quantity_dropdown',
      'quantity_manual_input': 'quantity_manual_input',
      'saved_gear_details': 'saved_gear_details_line_item',
      'saved_gear_details_all': 'saved_gear_details_all',
      'order_details': 'order_details_line_item',
      'order_details_all': 'order_details_all'
    }

    return originMapping[originValue] || originValue
  }

  private isLicensedProduct (baseProductSku: string): boolean {
    if (!baseProductSku) {
      return false
    }

    const firstThreeDigits = baseProductSku.substring(0, 3)
    const numericValue = parseInt(firstThreeDigits, 10)
    return numericValue >= 800 && numericValue <= 899
  }

  private calculateSubtotal (cartOrOrder: Cart | Order, useSubtotalWithoutDiscounts: boolean = false): string {
    let subtotal = useSubtotalWithoutDiscounts
      ? cartOrOrder.subTotalWithoutDiscounts?.value || 0
      : cartOrOrder.subTotal?.value || 0

    if (cartOrOrder.groupedFeeProductResults?.length) {
      const bygCosts = cartOrOrder.groupedFeeProductResults.reduce((total, fee) => {
        return total + (fee.price?.value || 0)
      }, 0)
      subtotal += bygCosts
    }

    return subtotal.toFixed(2)
  }

  private extractTrueValue (value: string | number | undefined): string {
    if (value === undefined || value === null) {
      return ''
    }

    if (typeof value === 'number') {
      return value.toFixed(2)
    }

    const numericString = value.toString().replace(/[^\d.-]/g, '')
    const numericValue = parseFloat(numericString)

    return isNaN(numericValue) ? '' : numericValue.toFixed(2)
  }

  private getProductRegularPrice (product: any): string {
    if (product?.msrpRange?.minPrice?.value) {
      return this.calculatePriceRange(product.msrpRange)
    } else if (product?.msrp?.value) {
      return product.msrp.value.toString()
    } else if (product?.nonCloseoutPriceRange?.minPrice?.value) {
      return product.nonCloseoutPriceRange.minPrice.value.toString()
    } else {
      return product?.price?.value?.toString() || ''
    }
  }

  public setUserLoggedIn (): void {
    this.setStoredLoginStatus('logged-in')
  }

  public setUserLoggedOut (): void {
    this.setStoredLoginStatus('logged-out')
  }

  private getStoredLoginStatus (): 'logged-in' | 'logged-out' | 'anonymous' {
    if (this.windowRef.isBrowser() && this.windowRef.nativeWindow?.localStorage) {
      const stored = this.windowRef.nativeWindow.localStorage.getItem('ccg_login_status')
      if (stored === 'logged-in' || stored === 'logged-out') {
        return stored
      }
    }
    return 'anonymous'
  }

  private setStoredLoginStatus (status: 'logged-in' | 'logged-out' | 'anonymous'): void {
    if (this.windowRef.isBrowser() && this.windowRef.nativeWindow?.localStorage) {
      this.windowRef.nativeWindow.localStorage.setItem('ccg_login_status', status)
    }
  }

  private getLoginStatus (): 'logged-in' | 'logged-out' | 'anonymous' {
    const hasValidToken = this.authStorageService.getItem('access_token')
    const hasStoredToken = this.authStorageService.getItem('refresh_token')
    const storedStatus = this.getStoredLoginStatus()

    if (hasValidToken) {
      this.setStoredLoginStatus('logged-in')
      return 'logged-in'
    } else if (hasStoredToken && storedStatus === 'logged-in') {
      this.setStoredLoginStatus('logged-out')
      return 'logged-out'
    } else if (storedStatus === 'logged-out') {
      return 'logged-out'
    } else {
      this.setStoredLoginStatus('anonymous')
      return 'anonymous'
    }
  }

  private getProductProofValue (bygType: string | null, requestProof: boolean): string | null {
    if (!bygType || !requestProof) {
      return null
    }

    if (bygType === 'LOCATION') {
      return 'emb'
    } else if (bygType === 'SPLOCATION') {
      return 'scp'
    }

    return null
  }

  asmAgentSignIn(agentId: string) {
    this.track('link', {
      event_name: 'asm_agent_logon',
      asm_agent_id: agentId
    })
  }

  asmCustomerEmulationStarted(agentId: string | null, customerName: string | undefined) {
    this.track('link', {
      event_name: 'asm_start_emulation',
      asm_agent_id: agentId,
      asm_cust: customerName
    })
  }

  asmCustomerEmulationEnded(agentId: string | null, customerName: string | null) {
    this.track('link', {
      event_name: 'asm_stop_emulation',
      asm_agent_id: agentId,
      asm_cust: customerName
    })
  }

  appendAsmSessionDetails() {
    const asmDetails: any = {}
    const asmLoggedInAgent = this.windowService.nativeWindow.sessionStorage.getItem('as_agentId')
    const asmEmulatedCustomer = this.windowService.nativeWindow.sessionStorage.getItem('emulatedCustName')
    if (asmLoggedInAgent) {
      asmDetails.asm_agent_id = asmLoggedInAgent
    }
    if (asmEmulatedCustomer) {
      asmDetails.asm_cust = asmEmulatedCustomer
    }
    return asmDetails
  }
}
